/**
 * Large super-admin tenant CRUD UI; kept as one module until split into smaller components.
 */
/* eslint-disable max-lines-per-function, complexity */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Search,
  MapPin,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { tenantAPI, Tenant, CreateTenantData, UpdateTenantData } from '@/services/api/tenants';
import { useAllTenants } from '@/hooks/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { KNOWN_OEMS } from '@/constants/oems';
import { useZonesPaginated } from '../hooks/useZones';

const DEFAULT_TENANTS_PAGE_SIZE = 5;
const TENANTS_PAGE_SIZE_OPTIONS = [5, 10, 25, 50] as const;

const TENANTS_CARD_HEADER_CLASS =
  'flex flex-col gap-4 space-y-0 md:flex-row md:items-start md:justify-between';
const TENANTS_PAGINATION_FOOTER_CLASS =
  'flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between';

type TenantTypeTab = 'all' | 'internal' | 'dealer';

function zoneIdsStillValidForOems(
  zoneIds: string[],
  zones: { id: string; oem: string }[],
  oems: string[]
): string[] {
  return zoneIds.filter(zoneId => zones.some(z => z.id === zoneId && oems.includes(z.oem)));
}

export const TenantSetupTab: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    data: tenants,
    isLoading: tenantsLoading,
    error: tenantsError,
    refetch
  } = useAllTenants();

  const [searchTerm, setSearchTerm] = useState('');
  const [tenantTypeTab, setTenantTypeTab] = useState<TenantTypeTab>('dealer');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [tenantsPageIndex, setTenantsPageIndex] = useState(0);
  const [tenantsPageSize, setTenantsPageSize] = useState(DEFAULT_TENANTS_PAGE_SIZE);

  // Form state
  const [formData, setFormData] = useState<{
    tenant_name: string;
    tenant_slug: string;
    tenant_type: string;
    status: string;
    time_zone: string;
    oems: string[]; // Multi-OEM support: array of OEM names
    description: string;
    primary_email: string;
    primary_phone: string;
    website: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: string;
    longitude: string;
    zone_ids: string[];
  }>({
    tenant_name: '',
    tenant_slug: '',
    tenant_type: '',
    status: 'active',
    time_zone: '',
    oems: [] as string[], // Multi-OEM: start with empty array
    description: '',
    primary_email: '',
    primary_phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    latitude: '',
    longitude: '',
    zone_ids: [] as string[]
  });

  // Fetch zones filtered by OEMs using same hook as Zone Setup
  // Uses high limit to get all zones for selected OEMs (typically < 100 per OEM)
  const oemsForQuery =
    formData.tenant_type === 'internal'
      ? undefined
      : formData.oems.length > 0
        ? formData.oems
        : undefined;
  const { data: zonesData, isLoading: loadingZones } = useZonesPaginated(
    1, // page
    500, // limit - high enough to get all zones for selected OEMs
    undefined, // searchTerm
    'active', // statusFilter - only active zones
    undefined, // regionFilter
    oemsForQuery, // oemFilter - filter by tenant's OEMs (undefined for internal = all zones)
    false // includeUserNames - not needed for selection
  );

  // Extract zones array from paginated response
  const filteredZones = zonesData?.zones ?? [];

  const openCreateDialog = () => {
    setEditingTenant(null);
    setFormData({
      tenant_name: '',
      tenant_slug: '',
      tenant_type: '',
      status: 'active',
      time_zone: '',
      oems: [] as string[], // Multi-OEM: start with empty array
      description: '',
      primary_email: '',
      primary_phone: '',
      website: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      latitude: '',
      longitude: '',
      zone_ids: [] as string[]
    });
    setDialogOpen(true);
  };

  const openEditDialog = async (tenant: Tenant) => {
    setEditingTenant(tenant);

    // Load zones for this tenant
    let zoneIds: string[] = tenant.zone_ids || [];
    try {
      const zones = await tenantAPI.getTenantZones(tenant.id);
      if (zones.length > 0) {
        zoneIds = zones.map(z => z.id);
      }
    } catch (error) {
      // Use zone_ids from tenant object if available
    }

    setFormData({
      tenant_name: tenant.tenant_name || '',
      tenant_slug: tenant.tenant_slug || '',
      tenant_type: tenant.tenant_type || '',
      status: tenant.status || 'active',
      time_zone: tenant.time_zone || '',
      // Multi-OEM: prefer oems array, fallback to single oem for backward compat
      oems: tenant.oems && tenant.oems.length > 0 ? tenant.oems : tenant.oem ? [tenant.oem] : [],
      description: tenant.description || '',
      primary_email: tenant.primary_email || '',
      primary_phone: tenant.primary_phone || '',
      website: tenant.website || '',
      address_line1: tenant.address_line1 || '',
      address_line2: tenant.address_line2 || '',
      city: tenant.city || '',
      state: tenant.state || '',
      postal_code: tenant.postal_code || '',
      country: tenant.country || 'US',
      latitude: tenant.latitude?.toString() || '',
      longitude: tenant.longitude?.toString() || '',
      zone_ids: zoneIds
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.tenant_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Tenant name is required',
        variant: 'destructive'
      });
      return;
    }

    // Validate at least one OEM is required for dealer-type tenants
    if (formData.tenant_type === 'dealer') {
      if (formData.oems.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'At least one OEM is required for dealer-type tenants',
          variant: 'destructive'
        });
        return;
      }
    }

    // Validate coordinates are required for dealer-type tenants
    if (formData.tenant_type === 'dealer') {
      if (
        !formData.latitude ||
        !formData.longitude ||
        formData.latitude.trim() === '' ||
        formData.longitude.trim() === ''
      ) {
        toast({
          title: 'Validation Error',
          description: 'Latitude and longitude are required for dealer-type tenants',
          variant: 'destructive'
        });
        return;
      }

      // Validate coordinate ranges
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);

      if (isNaN(lat) || lat < -90 || lat > 90) {
        toast({
          title: 'Validation Error',
          description: 'Latitude must be between -90 and 90',
          variant: 'destructive'
        });
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        toast({
          title: 'Validation Error',
          description: 'Longitude must be between -180 and 180',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      setFormLoading(true);

      if (editingTenant) {
        // Update existing tenant
        const updateData: UpdateTenantData = {
          tenant_name: formData.tenant_name.trim(),
          tenant_slug: formData.tenant_slug.trim() || undefined,
          tenant_type: formData.tenant_type || undefined,
          status: formData.status,
          time_zone: formData.time_zone || undefined,
          oems: formData.oems.length > 0 ? formData.oems : undefined, // Multi-OEM array
          description: formData.description || undefined,
          primary_email: formData.primary_email || undefined,
          primary_phone: formData.primary_phone || undefined,
          website: formData.website || undefined,
          address_line1: formData.address_line1 || undefined,
          address_line2: formData.address_line2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postal_code || undefined,
          country: formData.country || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          zone_ids: formData.zone_ids
        };

        await tenantAPI.updateTenant(editingTenant.id, updateData);

        toast({
          title: 'Tenant Updated',
          description: `Tenant "${formData.tenant_name}" has been updated successfully`
        });
      } else {
        // Create new tenant
        const createData: CreateTenantData = {
          tenant_name: formData.tenant_name.trim(),
          tenant_slug: formData.tenant_slug.trim() || undefined,
          tenant_type: formData.tenant_type || undefined,
          status: formData.status,
          time_zone: formData.time_zone || undefined,
          oems: formData.oems.length > 0 ? formData.oems : undefined, // Multi-OEM array
          description: formData.description || undefined,
          primary_email: formData.primary_email || undefined,
          primary_phone: formData.primary_phone || undefined,
          website: formData.website || undefined,
          address_line1: formData.address_line1 || undefined,
          address_line2: formData.address_line2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postal_code || undefined,
          country: formData.country || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          zone_ids: formData.zone_ids
        };

        await tenantAPI.createTenant(createData);

        toast({
          title: 'Tenant Created',
          description: `Tenant "${formData.tenant_name}" has been created successfully`
        });
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tenants'] });

      setDialogOpen(false);
      await refetch();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${editingTenant ? 'update' : 'create'} tenant`,
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (tenant: Tenant) => {
    // eslint-disable-next-line no-alert -- destructive action; replace with modal when UX allows
    const ok = window.confirm(`Delete tenant "${tenant.tenant_name}"? This cannot be undone.`);
    if (!ok) {
      return;
    }

    try {
      await tenantAPI.deleteTenant(tenant.id, false); // Soft delete

      toast({
        title: 'Tenant Deleted',
        description: `Tenant "${tenant.tenant_name}" has been deleted`
      });

      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      await refetch();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete tenant',
        variant: 'destructive'
      });
    }
  };

  const toggleZone = (zoneId: string) => {
    setFormData(prev => ({
      ...prev,
      zone_ids: prev.zone_ids.includes(zoneId)
        ? prev.zone_ids.filter(z => z !== zoneId)
        : [...prev.zone_ids, zoneId]
    }));
  };

  // Distinct tenant_type values from data (dialog shows extras beyond fixed SelectItem list)
  const tenantTypes = React.useMemo(() => {
    if (!tenants) return [];
    const types = new Set<string>();
    tenants.forEach(t => {
      if (t.tenant_type) {
        types.add(t.tenant_type);
      }
    });
    return Array.from(types).sort();
  }, [tenants]);

  // Filter tenants
  const filteredTenants = React.useMemo(() => {
    if (!tenants) return [];

    return tenants.filter(tenant => {
      // Search filter
      const matchesSearch =
        !searchTerm ||
        tenant.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_type?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTenantTypeTab = tenantTypeTab === 'all' || tenant.tenant_type === tenantTypeTab;

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && tenant.status === 'active') ||
        (statusFilter === 'inactive' && tenant.status !== 'active');

      return matchesSearch && matchesTenantTypeTab && matchesStatus;
    });
  }, [tenants, searchTerm, tenantTypeTab, statusFilter]);

  const filteredTenantCount = filteredTenants.length;
  const tenantsTotalPages = Math.max(1, Math.ceil(filteredTenantCount / tenantsPageSize));
  const tenantsSafePageIndex = Math.min(tenantsPageIndex, tenantsTotalPages - 1);
  const tenantsPageStart = tenantsSafePageIndex * tenantsPageSize;
  const paginatedTenants = filteredTenants.slice(
    tenantsPageStart,
    tenantsPageStart + tenantsPageSize
  );
  const tenantsRangeEnd =
    filteredTenantCount === 0
      ? 0
      : Math.min(tenantsPageStart + tenantsPageSize, filteredTenantCount);

  useEffect(() => {
    setTenantsPageIndex(0);
  }, [searchTerm, tenantTypeTab, statusFilter, tenantsPageSize]);

  useEffect(() => {
    setTenantsPageIndex(p => Math.min(p, Math.max(0, tenantsTotalPages - 1)));
  }, [tenantsTotalPages]);

  if (tenantsLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (tenantsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <AlertCircle className='h-5 w-5' />
            Error Loading Tenants
          </CardTitle>
          <CardDescription>
            {tenantsError instanceof Error ? tenantsError.message : 'Failed to load tenants'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant='outline'>
            <RefreshCw className='h-4 w-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header Actions */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold tracking-tight'>Tenant Management</h2>
          <p className='text-muted-foreground'>
            View, create, and configure tenants for the system
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className='h-4 w-4 mr-2' />
          Add Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and filter the tenant list by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='space-y-2'>
              <Label>Search</Label>
              <div className='relative'>
                <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search tenants...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-8'
                />
              </div>
            </div>
            <div className='space-y-2'>
              <Label>Status</Label>
              <Select
                value={statusFilter}
                onValueChange={v => setStatusFilter(v as 'all' | 'active' | 'inactive')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Statuses</SelectItem>
                  <SelectItem value='active'>Active</SelectItem>
                  <SelectItem value='inactive'>Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader className={TENANTS_CARD_HEADER_CLASS}>
          <div className='space-y-1.5'>
            <CardTitle>Tenants ({filteredTenants.length})</CardTitle>
            <CardDescription>Manage tenant configurations and zone associations</CardDescription>
          </div>
          <Tabs
            value={tenantTypeTab}
            onValueChange={v => setTenantTypeTab(v as TenantTypeTab)}
            className='w-full shrink-0 md:w-auto'
          >
            <TabsList className='grid h-9 w-full grid-cols-3 gap-1 md:flex md:w-auto'>
              <TabsTrigger value='internal' className='px-2 text-xs sm:text-sm'>
                Internal
              </TabsTrigger>
              <TabsTrigger value='dealer' className='px-2 text-xs sm:text-sm'>
                Dealer
              </TabsTrigger>
              <TabsTrigger value='all' className='px-2 text-xs sm:text-sm'>
                All tenants
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {filteredTenants.length === 0 ? (
            <div className='text-center py-12 text-muted-foreground'>
              <Building2 className='h-12 w-12 mx-auto mb-4 opacity-50' />
              <p>No tenants found</p>
              {tenants && tenants.length > 0 && (
                <p className='text-sm mt-2'>Try adjusting your filters</p>
              )}
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>OEM(s)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time Zone</TableHead>
                    <TableHead>Zones</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTenants.map(tenant => (
                    <TableRow key={tenant.id}>
                      <TableCell className='font-medium'>{tenant.tenant_name}</TableCell>
                      <TableCell>
                        <code className='text-xs bg-muted px-2 py-1 rounded'>
                          {tenant.tenant_slug || 'N/A'}
                        </code>
                      </TableCell>
                      <TableCell>
                        {tenant.tenant_type ? (
                          <Badge variant='outline'>{tenant.tenant_type}</Badge>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.oems && tenant.oems.length > 0 ? (
                          <div className='flex flex-wrap gap-1'>
                            {tenant.oems.slice(0, 2).map(oem => (
                              <Badge key={oem} variant='outline'>
                                {oem}
                              </Badge>
                            ))}
                            {tenant.oems.length > 2 && (
                              <Badge variant='outline'>+{tenant.oems.length - 2}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.status === 'active' ? (
                          <Badge variant='default' className='bg-green-500'>
                            <CheckCircle className='h-3 w-3 mr-1' />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant='secondary'>
                            <XCircle className='h-3 w-3 mr-1' />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.time_zone || <span className='text-muted-foreground'>—</span>}
                      </TableCell>
                      <TableCell>
                        {tenant.tenant_type === 'internal' ? (
                          <Badge
                            variant='outline'
                            className='text-xs bg-green-50 text-green-700 border-green-200'
                          >
                            <CheckCircle className='h-3 w-3 mr-1' />
                            All zones
                          </Badge>
                        ) : tenant.zones && tenant.zones.length > 0 ? (
                          <div className='flex flex-wrap gap-1'>
                            {tenant.zones.slice(0, 2).map(zone => (
                              <Badge key={zone.id} variant='outline' className='text-xs'>
                                {zone.zone_name}
                              </Badge>
                            ))}
                            {tenant.zones.length > 2 && (
                              <Badge variant='outline' className='text-xs'>
                                +{tenant.zones.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted-foreground text-sm'>No zones</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex items-center justify-end gap-2'>
                          <Button variant='ghost' size='sm' onClick={() => openEditDialog(tenant)}>
                            <Edit className='h-4 w-4' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDelete(tenant)}
                            className='text-destructive hover:text-destructive'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {filteredTenantCount > 0 && (
            <div className={TENANTS_PAGINATION_FOOTER_CLASS}>
              <p className='text-sm text-muted-foreground'>
                Showing <span className='font-medium text-foreground'>{tenantsPageStart + 1}</span>–
                <span className='font-medium text-foreground'>{tenantsRangeEnd}</span>
                <span> of </span>
                <span className='font-medium text-foreground'>{filteredTenantCount}</span>
              </p>
              <div className='flex flex-wrap items-center gap-2'>
                <Select
                  value={String(tenantsPageSize)}
                  onValueChange={v => setTenantsPageSize(Number(v))}
                >
                  <SelectTrigger className='w-[120px]'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TENANTS_PAGE_SIZE_OPTIONS.map(n => (
                      <SelectItem key={n} value={String(n)}>
                        {n} per page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-8 w-8'
                    disabled={tenantsSafePageIndex <= 0}
                    onClick={() => setTenantsPageIndex(p => Math.max(0, p - 1))}
                    aria-label='Previous page'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <span className='min-w-[7rem] text-center text-sm text-muted-foreground'>
                    Page {tenantsSafePageIndex + 1} of {tenantsTotalPages}
                  </span>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-8 w-8'
                    disabled={tenantsSafePageIndex >= tenantsTotalPages - 1}
                    onClick={() => setTenantsPageIndex(p => Math.min(tenantsTotalPages - 1, p + 1))}
                    aria-label='Next page'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Edit Tenant' : 'Create New Tenant'}</DialogTitle>
            <DialogDescription>
              {editingTenant
                ? 'Update tenant configuration and zone associations'
                : 'Configure a new tenant with type and zone associations'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='tenant_name'>
                  Tenant Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='tenant_name'
                  value={formData.tenant_name}
                  onChange={e => setFormData(prev => ({ ...prev, tenant_name: e.target.value }))}
                  placeholder='e.g., Acme Dealership'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='tenant_slug'>Slug</Label>
                <Input
                  id='tenant_slug'
                  value={formData.tenant_slug}
                  onChange={e => setFormData(prev => ({ ...prev, tenant_slug: e.target.value }))}
                  placeholder='e.g., acme-dealership'
                />
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='tenant_type'>Type</Label>
                <Select
                  value={
                    formData.tenant_type && formData.tenant_type.trim() !== ''
                      ? formData.tenant_type
                      : undefined
                  }
                  onValueChange={value => {
                    setFormData(prev => ({
                      ...prev,
                      tenant_type: value,
                      zone_ids: value === 'internal' ? [] : prev.zone_ids
                    }));
                  }}
                >
                  <SelectTrigger id='tenant_type'>
                    <SelectValue placeholder='Select type' />
                  </SelectTrigger>
                  <SelectContent className='z-[10000]'>
                    <SelectItem value='dealer'>Dealer</SelectItem>
                    <SelectItem value='internal'>Internal</SelectItem>
                    <SelectItem value='partner'>Partner</SelectItem>
                    <SelectItem value='test'>Test</SelectItem>
                    {tenantTypes
                      .filter(type => !['dealer', 'internal', 'partner', 'test'].includes(type))
                      .map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='status'>Status</Label>
                <Select
                  value={formData.status || undefined}
                  onValueChange={value => {
                    setFormData(prev => ({ ...prev, status: value }));
                  }}
                >
                  <SelectTrigger id='status'>
                    <SelectValue placeholder='Select status' />
                  </SelectTrigger>
                  <SelectContent className='z-[10000]'>
                    <SelectItem value='active'>Active</SelectItem>
                    <SelectItem value='inactive'>Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>
                  OEM{formData.oems.length > 1 ? 's' : ''}
                  {formData.tenant_type === 'dealer' && (
                    <span className='text-destructive ml-1'>*</span>
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant='outline'
                      className='w-full justify-between font-normal'
                      type='button'
                    >
                      {formData.oems.length === 0 ? (
                        <span className='text-muted-foreground'>Select OEM(s)...</span>
                      ) : formData.oems.length === 1 ? (
                        formData.oems[0]
                      ) : (
                        `${formData.oems.length} OEMs selected`
                      )}
                      <ChevronDown className='h-4 w-4 ml-2 opacity-50' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-[300px] max-h-[300px] overflow-y-auto z-[10000]'
                    align='start'
                  >
                    <div className='space-y-2'>
                      {KNOWN_OEMS.map(oem => (
                        <div key={oem} className='flex items-center space-x-2'>
                          <Checkbox
                            id={`oem-${oem}`}
                            checked={formData.oems.includes(oem)}
                            onCheckedChange={checked => {
                              setFormData(prev => {
                                const newOems = checked
                                  ? [...prev.oems, oem]
                                  : prev.oems.filter(o => o !== oem);

                                // When removing an OEM, also remove zones that belong to that OEM
                                const validZoneIds = checked
                                  ? prev.zone_ids
                                  : zoneIdsStillValidForOems(prev.zone_ids, filteredZones, newOems);

                                return {
                                  ...prev,
                                  oems: newOems,
                                  zone_ids: validZoneIds
                                };
                              });
                            }}
                          />
                          <Label
                            htmlFor={`oem-${oem}`}
                            className='font-normal cursor-pointer flex-1'
                          >
                            {oem}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {formData.tenant_type === 'dealer' && (
                  <p className='text-xs text-muted-foreground'>
                    <span className='text-destructive'>At least one required for dealers</span>
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='time_zone'>Time Zone</Label>
                <Select
                  value={
                    formData.time_zone && formData.time_zone.trim() !== ''
                      ? formData.time_zone
                      : undefined
                  }
                  onValueChange={value => {
                    setFormData(prev => ({ ...prev, time_zone: value }));
                  }}
                >
                  <SelectTrigger id='time_zone'>
                    <SelectValue placeholder='Select time zone' />
                  </SelectTrigger>
                  <SelectContent className='z-[10000]'>
                    <SelectItem value='America/New_York'>Eastern Time (ET)</SelectItem>
                    <SelectItem value='America/Chicago'>Central Time (CT)</SelectItem>
                    <SelectItem value='America/Denver'>Mountain Time (MT)</SelectItem>
                    <SelectItem value='America/Phoenix'>Mountain Time - Arizona (MST)</SelectItem>
                    <SelectItem value='America/Los_Angeles'>Pacific Time (PT)</SelectItem>
                    <SelectItem value='America/Anchorage'>Alaska Time (AKT)</SelectItem>
                    <SelectItem value='Pacific/Honolulu'>Hawaii Time (HST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='country'>Country</Label>
                <Input
                  id='country'
                  value={formData.country}
                  onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder='e.g., US'
                />
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder='Tenant description...'
                rows={3}
              />
            </div>

            <div className='border-t pt-4'>
              <h3 className='text-sm font-semibold mb-4'>Contact Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='primary_email'>Primary Email</Label>
                  <Input
                    id='primary_email'
                    type='email'
                    value={formData.primary_email}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, primary_email: e.target.value }))
                    }
                    placeholder='contact@example.com'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='primary_phone'>Primary Phone</Label>
                  <Input
                    id='primary_phone'
                    value={formData.primary_phone}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, primary_phone: e.target.value }))
                    }
                    placeholder='+1 (555) 123-4567'
                  />
                </div>
              </div>
              <div className='space-y-2 mt-4'>
                <Label htmlFor='website'>Website</Label>
                <Input
                  id='website'
                  type='url'
                  value={formData.website}
                  onChange={e => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder='https://www.example.com'
                />
              </div>
            </div>

            <div className='border-t pt-4'>
              <h3 className='text-sm font-semibold mb-4'>Address Information</h3>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='address_line1'>Address Line 1</Label>
                  <Input
                    id='address_line1'
                    value={formData.address_line1}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, address_line1: e.target.value }))
                    }
                    placeholder='Street address'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='address_line2'>Address Line 2</Label>
                  <Input
                    id='address_line2'
                    value={formData.address_line2}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, address_line2: e.target.value }))
                    }
                    placeholder='Suite, unit, building, floor, etc.'
                  />
                </div>
                <div className='grid grid-cols-3 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>City</Label>
                    <Input
                      id='city'
                      value={formData.city}
                      onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder='City'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='state'>State/Province</Label>
                    <Input
                      id='state'
                      value={formData.state}
                      onChange={e => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder='State'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='postal_code'>Postal Code</Label>
                    <Input
                      id='postal_code'
                      value={formData.postal_code}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, postal_code: e.target.value }))
                      }
                      placeholder='ZIP/Postal'
                    />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4 mt-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='latitude'>
                      Latitude
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>*</span>
                      )}
                    </Label>
                    <Input
                      id='latitude'
                      type='number'
                      step='any'
                      value={formData.latitude}
                      onChange={e => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                      placeholder='e.g., 40.7128'
                      required={formData.tenant_type === 'dealer'}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Decimal degrees (-90 to 90)
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>Required for dealers</span>
                      )}
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='longitude'>
                      Longitude
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>*</span>
                      )}
                    </Label>
                    <Input
                      id='longitude'
                      type='number'
                      step='any'
                      value={formData.longitude}
                      onChange={e => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                      placeholder='e.g., -74.0060'
                      required={formData.tenant_type === 'dealer'}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Decimal degrees (-180 to 180)
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>Required for dealers</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='space-y-2 border-t pt-4'>
              <Label>Zones</Label>
              {formData.tenant_type === 'internal' ? (
                <div className='border rounded-md p-4 bg-muted/50'>
                  <div className='flex items-start gap-3'>
                    <CheckCircle className='h-5 w-5 text-green-600 mt-0.5 flex-shrink-0' />
                    <div>
                      <p className='text-sm font-medium text-foreground'>
                        All zones available by default
                      </p>
                      <p className='text-xs text-muted-foreground mt-1'>
                        Internal tenants automatically have access to all zones. Zone selection is
                        not required.
                      </p>
                    </div>
                  </div>
                </div>
              ) : formData.oems.length === 0 ? (
                <div className='border rounded-md p-4 bg-muted/50'>
                  <div className='flex items-start gap-3'>
                    <Info className='h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0' />
                    <div>
                      <p className='text-sm font-medium text-foreground'>Select OEM(s) first</p>
                      <p className='text-xs text-muted-foreground mt-1'>
                        Zones are filtered by OEM. Please select one or more OEMs above to see
                        available zones.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='border rounded-md p-4 max-h-64 overflow-y-auto'>
                  {loadingZones ? (
                    <div className='flex items-center justify-center py-8'>
                      <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                    </div>
                  ) : filteredZones.length === 0 ? (
                    <p className='text-sm text-muted-foreground text-center py-4'>
                      No zones available for {formData.oems.join(', ')}. Zones are automatically
                      detected from data collection tasks.
                    </p>
                  ) : (
                    <div className='space-y-2'>
                      {filteredZones.map(zone => (
                        <div key={zone.id} className='flex items-center space-x-2'>
                          <Checkbox
                            id={`zone-${zone.id}`}
                            checked={formData.zone_ids.includes(zone.id)}
                            onCheckedChange={() => toggleZone(zone.id)}
                          />
                          <Label
                            htmlFor={`zone-${zone.id}`}
                            className='font-normal cursor-pointer flex-1'
                          >
                            <div className='flex items-center gap-2'>
                              <MapPin className='h-4 w-4 text-muted-foreground' />
                              <span className='font-medium'>{zone.zone_name}</span>
                              {formData.oems.length > 1 && (
                                <Badge variant='outline' className='text-xs'>
                                  {zone.oem}
                                </Badge>
                              )}
                              <code className='text-xs text-muted-foreground'>
                                ({zone.zone_slug})
                              </code>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                  {filteredZones.length > 0 && (
                    <div className='mt-4 pt-4 border-t'>
                      <p className='text-sm text-muted-foreground'>
                        Selected: {formData.zone_ids.length} of {filteredZones.length} zone
                        {filteredZones.length !== 1 ? 's' : ''}
                        {formData.oems.length === 1
                          ? ` (${formData.oems[0]})`
                          : ` across ${formData.oems.length} OEMs`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)} disabled={formLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading}>
              {formLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              {editingTenant ? 'Update' : 'Create'} Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
